import React, { useEffect, useRef } from "react";
import { useState } from "react";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumbs from "../components/Breadcrumbs";
import PatientAddress from "../components/PatientAddress";
import PatientBasicDetails from "../components/PatientBasicDetails";
import PatientEmergencyContact from "../components/PatientEmergencyContact";
import UserExistPopUp from "../components/UserExistPopUp";
import { API } from "../constants/api";
import { PAGE } from "../constants/page";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { ApiCallWithLoader, errorUpdate } from "../redux/reducers/LoaderSlice";
import { patientName } from "../redux/reducers/PatientSlice";
import { LoaderState } from "../redux/reducers/LoaderSlice";
import "../styles/AddPatient.scss";
import ToFhir from "../utility/ToFhir";
import Utility from "../utility/Utility";
import { UserState } from "src/redux/reducers/UserSlice";
import { stateRights } from "src/config/stateRights";
import AuthService from "../services/AuthService";
import HttpService from "../services/HttpService";

const NewPatient = (props: any) => {
  const httpService = HttpService.instance;
  const [basicDetails, setBasicDetails] = useState<any>({ status: "" });
  const [contactDetails, setContactDetails] = useState<any>({
    status: "inactive",
  });

  const [addressDetails, setAddressDetails] = useState({
    status: "inactive",
  });
  const [practitionerRoleIds, setPractitionerRoleIds] = useState<any>(null);
  const [displayCalender, setDisplayCalender] = useState(true);
  const [displayAge, setDisplayAge] = useState(true);
  const [showExistPopUp, setShowExistPopUp] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const loader = useAppSelector(LoaderState);
  const userDetails: any = useAppSelector(UserState);
  const state = userDetails?.userDetails?.user_metadata?.address?.region;
  const stateRight = stateRights[state?.replace(/ /g, "")];
  const city =
    userDetails?.userDetails?.user_metadata?.address?.locality?.split(",")[0];
  const district =
    userDetails?.userDetails?.user_metadata?.address?.locality?.split(",")[1];
  const pincode = userDetails?.userDetails?.user_metadata?.address?.postal_code;
  const searchParams = new URLSearchParams(location?.search);
  const contactDetail_form_fields = ["contactNumber", "name", "relationship"];
  const basicDetail_form_fields = [
    "gender",
    "firstName",
    "middleName",
    "lastName",
    "idNumber",
    "mobileNumber",
    "id",
    "age",
    "birthDate",
    "selectedOption",
  ];

  function scrollOnInputSelected() {
    if (
      document?.activeElement?.tagName == "INPUT" ||
      document?.activeElement?.tagName == "TEXTAREA" ||
      document?.activeElement?.tagName == "SELECT"
    ) {
      window.setTimeout(function () {
        (document?.activeElement as any)?.scrollIntoViewIfNeeded();
      }, 0);
    }
  }

  useEffect(() => {
    onRelationUpdate(contactDetails?.relationship);
  }, [
    basicDetails?.firstName,
    basicDetails?.lastName,
    basicDetails?.middleName,
    basicDetails?.mobileNumber,
  ]);

  const handleBackButtonOnContactTab = useRef(() => {
    window.removeEventListener(
      "popstate",
      handleBackButtonOnContactTab.current
    );
    navigate(PAGE.NEW_PATIENT + "?tab=1");
  });
  const handleBackButtonOnAddressTab = useRef(() => {
    window.removeEventListener(
      "popstate",
      handleBackButtonOnAddressTab.current
    );
    navigate(PAGE.NEW_PATIENT + "?tab=2");
  });

  useEffect(() => {
    getCachedData();
  }, [searchParams.get("tab")]);

  useEffect(() => {
    const checkIntrospect = async () => {
      const inrospectUrl =
        process.env.REACT_APP_PCPO_BACK_END_HEALTH_API + "/introspect";
      const authToken = { token: AuthService.instance.rawAuthToken };
      try {
        const introspectToken = await httpService.callIntrospect(
          inrospectUrl,
          "POST",
          authToken
        );
        if (introspectToken.active === true) {
          window.addEventListener("resize", scrollOnInputSelected);
          return function cleanupListener() {
            window.removeEventListener("resize", scrollOnInputSelected);
          };
        } else {
          throw {
            status: 400,
            statusCode: "introspectCheck",
            message: "introspect is active",
          };
        }
      } catch (error) {
        dispatch(errorUpdate({ isError: true, error: error }));
      }
    };
    checkIntrospect();

    return () => {
      window.removeEventListener("resize", scrollOnInputSelected);
    };
  }, []);

  const clearCachedData = () => {
    contactDetail_form_fields.map(val => {
      sessionStorage.removeItem(val);
    });
    basicDetail_form_fields.map(val => {
      sessionStorage.removeItem(val);
    });
    //sessionStorage.removeItem("street1");
    //sessionStorage.removeItem("street2");
    //sessionStorage.removeItem("pincode");
  };

  const getCachedData = () => {
    const basicData: any = {};
    const contactData: any = {};
    if (!searchParams.get("tab")) {
      clearCachedData();
    } else {
      basicDetail_form_fields.map(val => {
        basicData[val] = sessionStorage.getItem(val);
      });
      contactDetail_form_fields.map(val => {
        contactData[val] = sessionStorage.getItem(val);
      });
    }

    if (sessionStorage.getItem("selectedOption") === "age") {
      setDisplayAge(false);
    } else if (sessionStorage.getItem("selectedOption") === "dob") {
      setDisplayCalender(false);
    }

    switch (searchParams.get("tab")) {
      case "1":
        setBasicDetails({ ...basicDetails, ...basicData, status: "active" });
        setContactDetails({
          ...contactDetails,
          ...contactData,
          status: "inactive",
        });
        setAddressDetails({ ...{}, status: "inactive" });
        break;
      case "2":
        setContactDetails({
          ...contactDetails,
          ...contactData,
          status: "active",
        });
        setBasicDetails({ ...basicDetails, ...basicData, status: "complete" });
        setAddressDetails({ ...{}, status: "inactive" });
        break;
      default:
        setBasicDetails({ ...{}, status: "active" });
        break;
    }
  };

  useEffect(() => {
    async function submitData() {
      clearCachedData();
      const resp = await createNewPatient();
      if (resp) {
        setAddressDetails({
          ...addressDetails,
          status: "complete",
        });
      }
    }

    if (addressDetails.status === "submitting") {
      window.removeEventListener(
        "popstate",
        handleBackButtonOnContactTab.current
      );
      window.removeEventListener(
        "popstate",
        handleBackButtonOnAddressTab.current
      );
      submitData();
    }
  }, [addressDetails]);

  const checkIfUserExist = async (basicData: any) => {
    try {
      const searchQuery = Utility.uniqueListToQueryString(
        stateRight.uniquenessCheckList,
        basicData
      );
      const searchUrl = API.SEARCH + searchQuery;
      const requestDetails = {
        url: searchUrl,
        method: "GET",
      };
      const searchResult = await dispatch(ApiCallWithLoader(requestDetails));
      return searchResult?.payload?.total === 0 ? false : true;
    } catch (error) {}
  };

  const getBasicDetials = async (basicData: any) => {
    window.addEventListener("popstate", handleBackButtonOnContactTab.current);
    const isUserExist = await checkIfUserExist(basicData);
    if (!isUserExist) {
      setContactDetails({ ...contactDetails, status: "active" });
      setBasicDetails({ ...basicDetails, ...basicData, status: "complete" });
      setAddressDetails({ ...addressDetails, status: "inactive" });
      window.scrollTo(0, 0);
    } else {
      setShowExistPopUp(true);
    }
  };

  const getContactDetails = (contactData: any) => {
    window.addEventListener("popstate", handleBackButtonOnAddressTab.current);

    //const street1 = sessionStorage.getItem("street1");
    //const street2 = sessionStorage.getItem("street2");
    setAddressDetails({
      ...{ addressDetails, state, district, city, pincode },
      status: "active",
    });
    setContactDetails({
      ...contactDetails,
      ...contactData,
      status: "complete",
    });
    setBasicDetails({ ...basicDetails, status: "complete" });
    window.scrollTo(0, 0);
  };

  const getAddressDetails = async (addressData: any) => {
    setBasicDetails({ ...basicDetails, status: "complete" });
    setContactDetails({ ...contactDetails, status: "complete" });
    setAddressDetails({
      ...addressDetails,
      ...addressData,
      status: "submitting",
    });
    window.scrollTo(0, 0);
  };

  const getPractitionerRoleIds = async (practitionerRoleIds: string[]) => {
    setPractitionerRoleIds(practitionerRoleIds);
  };

  const createNewPatient = async () => {
    const postData = {
      resourceType: "Patient",
      name: [ToFhir.patientName(basicDetails.firstName, basicDetails?.middleName, basicDetails.lastName)],
      gender: basicDetails.gender,
      birthDate: basicDetails.birthDate,
      address: ToFhir.address(addressDetails),
      generalPractitioner: await ToFhir.generalPractitioner(
        practitionerRoleIds
      ),
      telecom: ToFhir.telecom(basicDetails.mobileNumber),
      ...(contactDetails?.relationship !== "" && {
        contact: ToFhir.contact(contactDetails),
      }),
      ...(basicDetails.idNumber && {
        identifier: ToFhir.identifier(
          basicDetails.id,
          basicDetails.idNumber.toLowerCase()
        ),
      }),
    };
    try {
      const requestDetails = {
        url: API.NEW_PATIENT,
        method: "POST",
        requestData: postData,
      };
      const resp = await dispatch(ApiCallWithLoader(requestDetails));
      dispatch(patientName({ ...basicDetails, newPatientId: resp.payload.id }));
      navigate(PAGE.HOME);
      return resp.payload;
    } catch (error) {
      dispatch(errorUpdate({ isError: true, error: error }));
    }
  };

  const handlePreviousClickonContactsDetails = (contactInfo: any) => {
    navigate(-1);
  };

  const onRelationUpdate = async (option: any) => {
    if (option === "self") {
      await setContactDetails({
        status: "",
      });
      let fName = basicDetails?.firstName;
      let mName = basicDetails?.middleName;
      let lName = basicDetails?.lastName;
      let pName = fName + ((mName) ? " " + mName : "") + " " + lName;

      setContactDetails({
        ...contactDetails,
        contactNumber: basicDetails?.mobileNumber,
        name: pName,
        relationship: "self",
        status: "active",
      });
    } else {
      await setContactDetails({
        status: "",
      });
      setContactDetails({
        ...contactDetails,
        contactNumber: "",
        name: "",
        relationship: option,
      });
    }
  };

  const handlePreviousClickonPatientDetails = (addressInfo: any) => {
    navigate(-1);
  };

  return (
    <div className="new-patient">
      <Breadcrumbs
        active_key="add_patient"
        navigation={navigate}
        location={location.pathname}
      />
      <div className="container">
        <h3 className="heading-bold mb-0">
          <Trans i18nKey={"add_patient"}></Trans>
        </h3>
      </div>

      <div className="bg-light-gray nav">
        <div className="container">
          <div className="row pt-3 mb-3 ms-2 me-2">
            <div className={`col p-0 ${basicDetails.status}`}>
              <div className={`mb-2 bg`}>
                <div className="d-inline-block position-relative">
                  <span>1</span>
                  <img alt="tick" src="/icons/tick_with_fill.svg" />
                </div>
              </div>
              <span
                className="d-inline-block pt-1 nav-heading"
                dangerouslySetInnerHTML={{
                  __html: Utility.breakWord(1, "basic_details"),
                }}
              ></span>
            </div>

            <div className={`col p-0 text-center ${contactDetails.status}`}>
              <div className={`mb-2 bg`}>
                <div className="d-inline-block position-relative">
                  <span style={{ left: "-17px" }}>2</span>
                  <img alt="tick" src="/icons/tick_with_fill.svg" />
                </div>
              </div>
              <span
                className="d-inline-block pt-1 nav-heading"
                dangerouslySetInnerHTML={{
                  __html: Utility.breakWord(1, "emergency_contact_details"),
                }}
              ></span>
            </div>
            <div className={`col p-0 text-end ${addressDetails.status}`}>
              <div className={`mb-2 bg`}>
                <div className="d-inline-block position-relative">
                  <span style={{ left: "-35px" }}>3</span>
                  <img alt="tick" src="/icons/tick_with_fill.svg" />
                </div>
              </div>
              <span
                className="d-inline-block pt-1 nav-heading"
                dangerouslySetInnerHTML={{
                  __html: Utility.breakWord(1, "address_details"),
                }}
              ></span>
            </div>
          </div>
        </div>
      </div>

      <div className="container ">
        {!loader.loading && (
          <p className="mandatoryField">
            <span>*</span>
            <Trans i18nKey={"mandatory_fields"}></Trans>
          </p>
        )}

        {basicDetails.status === "active" && (
          <PatientBasicDetails
            onBasicDetails={getBasicDetials}
            defaultValues={basicDetails}
            dob={displayCalender}
            setDob={setDisplayCalender}
            age={displayAge}
            setAge={setDisplayAge}
          />
        )}
        {contactDetails.status === "active" && (
          <PatientEmergencyContact
            dob={basicDetails.birthDate}
            onPreviousClick={handlePreviousClickonContactsDetails}
            onEmergencyContactDetails={getContactDetails}
            defaultValues={contactDetails}
            dropDownClick={onRelationUpdate}
          />
        )}
        {addressDetails.status === "active" && (
          <PatientAddress
            onAddressDetails={getAddressDetails}
            onPractitionerRoleDetails={getPractitionerRoleIds}
            onPreviousClick={handlePreviousClickonPatientDetails}
            defaultValues={addressDetails}
            basicDetails={basicDetails}
          />
        )}
        <UserExistPopUp
          show={showExistPopUp}
          handelClose={() => {
            setShowExistPopUp(false);
          }}
          title_key={"duplicate_record"}
          body_key="duplicate_record_message"
          secondaryButtonTitle_key="close"
        />
      </div>
    </div>
  );
};

export default NewPatient;
